import { MetricUnit, SmaMetricName } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { useSelector } from '@/store';
import { useSuspenseQuery } from '@apollo/client';
import ModalTitle from '@components/modal/ModalTitle';
import GET_SMA_REPORTS from 'src/features/devices2/graphql/queries/getSMAData';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { CheckCircle, ContentCopy, Error } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Suspense, useEffect, useState, useTransition, type FC } from 'react';
import { useDispatch } from 'react-redux';
import { useModalStatusMessage } from 'src/hooks';
import { setSmaReportsTableModal } from 'src/slices/modals';
import SendReportsByEmailModal from './SendReportsByEmailModal';

interface Props {
  deviceId: string;
  smaUnitId: string;
  smaDeviceId: string;
  smaProcessId: string;
  smaMetricName: SmaMetricName;
  onClose: () => void;
}

const PAGE_LIMIT = 10;

const getUnit = (unit?: string | null) => {
  if (unit === 'l/s') return MetricUnit.LiterPerSecond;
  return MetricUnit.Centimeter;
};

const Component: FC<Props> = ({
  deviceId,
  smaUnitId,
  smaDeviceId,
  smaProcessId,
  smaMetricName,
  onClose,
}) => {
  const { _ } = useLingui();
  const { formatDateTime, formatMetricValue } = useFormatters();
  const { openModalSuccessMessage } = useModalStatusMessage();
  const [openSendReportsModal, setOpenSendReportsModal] = useState(false);
  const [pending, startTransition] = useTransition();
  const [pagination, setPagination] = useState({
    limit: PAGE_LIMIT,
    page: 1,
    loadedPages: 0,
    hasNextPage: true,
  });

  const { data, fetchMore } = useSuspenseQuery(GET_SMA_REPORTS, {
    variables: {
      inputDevice: {
        id: deviceId,
      },
      inputSmaConfig: {
        smaUnitId,
        smaDeviceId,
        smaProcessId,
        smaMetricName,
      },
      inputPagination: {
        limit: pagination.limit,
      },
    },
  });

  useEffect(() => {
    if (pending) return;
    if (!data.device.smaConfig) return;

    const totalCount = data.device.smaConfig?.[0].reports.items.length ?? 0;
    const hasNextPage = data.device.smaConfig?.[0].reports.pageInfo.lastEvaluatedKey != null;

    if (totalCount === 0 && !hasNextPage) {
      setPagination((prev) => ({ ...prev, loadedPages: 0, hasNextPage: false }));
      return;
    }

    if (Math.ceil(totalCount) / pagination.limit > pagination.loadedPages) {
      setPagination((prev) => ({
        ...prev,
        loadedPages: Math.ceil(totalCount / prev.limit),
        hasNextPage,
      }));
      return;
    }

    if (pagination.page > pagination.loadedPages && pagination.hasNextPage) {
      startTransition(() => {
        fetchMore({
          variables: {
            inputDevice: {
              id: deviceId,
            },
            inputPagination: {
              limit: pagination.limit,
              exclusiveStartKey: data.device.smaConfig?.[0]?.reports.pageInfo.lastEvaluatedKey,
            },
          },
          updateQuery(previousResult, { fetchMoreResult }) {
            if (!fetchMoreResult) return previousResult;
            if (!fetchMoreResult.device.smaConfig || !previousResult.device.smaConfig) {
              return {
                device: fetchMoreResult.device,
              };
            }
            return {
              device: {
                ...previousResult.device,
                smaConfig: previousResult.device.smaConfig.map((c) => {
                  return {
                    ...c,
                    reports: {
                      ...previousResult.device.smaConfig[0].reports,
                      items: [
                        ...previousResult.device.smaConfig[0].reports.items,
                        ...fetchMoreResult.device.smaConfig[0].reports.items,
                      ],
                      pageInfo: fetchMoreResult.device.smaConfig?.[0]?.reports.pageInfo,
                    },
                  };
                }),
              },
            };
          },
        });
      });
    }
  }, [pending, startTransition, data, pagination, setPagination, fetchMore, deviceId]);

  const rows = data.device.smaConfig?.[0]?.reports.items ?? [];

  return (
    <>
      <SendReportsByEmailModal
        deviceId={deviceId}
        deviceName={data.device.profile.name}
        open={openSendReportsModal}
        onClose={() => setOpenSendReportsModal(false)}
        smaUnitId={smaUnitId}
        smaProcessId={smaProcessId}
        smaMetricName={smaMetricName}
        smaDeviceId={smaDeviceId}
      />
      <ModalTitle
        onClose={onClose}
        title={
          <Box display="flex" gap={1}>
            <Trans>Tabla de reportes SMA</Trans> {data.device.profile.name}
          </Box>
        }
      />
      <DialogContent>
        <TableContainer
          sx={{
            height: 440,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell width={200}>
                  <Trans>Fecha</Trans>
                </TableCell>
                <TableCell width={200}>
                  <Trans>Métrica</Trans>
                </TableCell>
                <TableCell width={220}>
                  <Trans>Mensaje</Trans>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell colSpan={5}>
                    <Box height={295} display="grid" sx={{ placeItems: 'center' }}>
                      <Trans>No hay datos</Trans>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {pending &&
                Array.from({ length: pagination.limit }, (_, i) => (
                  <TableRow key={i} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Box>
                        <Skeleton variant="circular" width={22} height={22} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={200} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={200} />
                      <Skeleton variant="text" width={200} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={220} />
                    </TableCell>
                  </TableRow>
                ))}
              {rows
                .slice((pagination.page - 1) * pagination.limit, pagination.page * pagination.limit)
                .map((row) => {
                  return (
                    <TableRow
                      key={row.timestamp}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>
                        {row.success ? (
                          <Tooltip
                            placement="right"
                            title={
                              <span>
                                <Trans>Número comprobante: {row.smaResponseVerificationId}</Trans>
                              </span>
                            }
                          >
                            <CheckCircle color="success" sx={{ display: 'block' }} />
                          </Tooltip>
                        ) : (
                          <Error color="error" sx={{ display: 'block' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography fontSize="inherit">
                            {formatDateTime(row.timestamp)}
                          </Typography>
                          <Typography fontSize="inherit" color="text.secondary">
                            Ejecución: {formatDateTime(row.timestamp)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {row.smaMetricValue != null && (
                          <Typography fontSize="inherit">
                            <Trans>Caudal</Trans>:{' '}
                            <b>
                              {formatMetricValue(row.smaMetricValue, {
                                unit: getUnit(row.smaMetricUnit),
                                precision: 1,
                              })}
                            </b>
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {!row.success ? (
                          row.smaResponseMessage
                        ) : (
                          <Box display="flex" gap={1} alignItems="center">
                            <span>
                              <Trans>Comprobante: {row.smaResponseVerificationId}</Trans>
                            </span>
                            <Tooltip title={<Trans>Copiar comprobante</Trans>}>
                              <IconButton
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    row.smaResponseVerificationId ?? '',
                                  );
                                  openModalSuccessMessage(_(msg`¡Comprobante copiado!`));
                                }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button color="info" variant="outlined" onClick={() => setOpenSendReportsModal(true)}>
            <Trans>Descargar</Trans>
          </Button>
          <Pagination
            page={pagination.page}
            onChange={(_, page) => setPagination((prev) => ({ ...prev, page }))}
            count={pagination.loadedPages + Number(pagination.hasNextPage)}
            showFirstButton
          />
        </Box>
      </DialogContent>
    </>
  );
};

const Fallback: FC<{ onClose: () => void }> = ({ onClose }) => (
  <>
    <ModalTitle
      onClose={onClose}
      title={
        <Box display="flex" gap={1}>
          <Trans>Tabla de reportes SMA</Trans> <Skeleton variant="text" width={200} />
        </Box>
      }
    />
    <DialogContent dividers>
      <Skeleton variant="rounded" height={500} />
    </DialogContent>
  </>
);

const SMAReportsTableModal = () => {
  const dispatch = useDispatch();
  const { smaReportsTableModal } = useSelector((state) => state.modals_store);

  const onClose = () => {
    dispatch(
      setSmaReportsTableModal({
        open: false,
      }),
    );
  };

  return (
    <Dialog onClose={onClose} open={smaReportsTableModal.open} maxWidth="lg" fullWidth>
      <Suspense fallback={<Fallback onClose={onClose} />}>
        {smaReportsTableModal.open && (
          <Component
            onClose={onClose}
            deviceId={smaReportsTableModal.deviceId}
            smaUnitId={smaReportsTableModal.smaUnitId}
            smaDeviceId={smaReportsTableModal.smaDeviceId}
            smaProcessId={smaReportsTableModal.smaProcessId}
            smaMetricName={smaReportsTableModal.smaMetricName}
          />
        )}
      </Suspense>
    </Dialog>
  );
};

export default SMAReportsTableModal;
